import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './context/AuthContext';
import { FreeTrialProvider } from './context/FreeTrialContext';
import { WatchProgressProvider } from './context/WatchProgressContext';
import EmailVerificationModal from './components/EmailVerificationModal';
import { ApplicationStatusBanner } from './components/ApplicationStatusBanner';
import { Header } from './components/Header';
import { MediaEngine } from './components/MediaEngine';
import { FloatingPlayer } from './components/FloatingPlayer';
import { UploadManager } from './components/UploadManager';
import { ProtectedRoute } from './components/ProtectedRoute';

// Eagerly loaded — tiny, always needed on any page
import Landing from './pages/Landing';

// Lazy loaded — each page becomes a separate chunk, loaded only when visited
const Login               = lazy(() => import('./pages/Login'));
const Register            = lazy(() => import('./pages/Register'));
const BecomeCreator       = lazy(() => import('./pages/BecomeCreator'));
const ForgotPassword      = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail         = lazy(() => import('./pages/VerifyEmail'));
const Dashboard           = lazy(() => import('./pages/Dashboard'));
const Library             = lazy(() => import('./pages/Library'));
const EpisodePlayer       = lazy(() => import('./pages/EpisodePlayer'));
const VideoPlayer         = lazy(() => import('./pages/VideoPlayer'));
const Subscribe           = lazy(() => import('./pages/Subscribe'));
const SubscribeSuccess    = lazy(() => import('./pages/SubscribeSuccess'));
const Profile             = lazy(() => import('./pages/Profile'));
const LoginSessions       = lazy(() => import('./pages/LoginSessions'));
const CreatorDashboard    = lazy(() => import('./pages/CreatorDashboard'));
const AdminDashboard      = lazy(() => import('./pages/AdminDashboard'));
const Playlists           = lazy(() => import('./pages/Playlists'));
const PlaylistDetail      = lazy(() => import('./pages/PlaylistDetail'));
const SharedEpisode       = lazy(() => import('./pages/SharedEpisode'));
const Terms               = lazy(() => import('./pages/Terms'));
const PrivacyPolicy       = lazy(() => import('./pages/PrivacyPolicy'));
const CreatorAgreement    = lazy(() => import('./pages/CreatorAgreement'));
const ContentGuidelines   = lazy(() => import('./pages/ContentGuidelines'));

import './App.css';

// Minimal spinner shown while a lazy chunk is loading
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <FreeTrialProvider>
      <WatchProgressProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background pb-20">
          {/* MediaEngine - Single source of truth for audio/video playback */}
          <MediaEngine />

          <Header />
          <EmailVerificationModal />
          <ApplicationStatusBanner />
          <main>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/pro-tvurce" element={<BecomeCreator />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Library />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/library"
                  element={
                    <ProtectedRoute>
                      <Library />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/episode/:episodeId"
                  element={
                    <ProtectedRoute>
                      <EpisodePlayer />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/subscribe"
                  element={
                    <ProtectedRoute>
                      <Subscribe />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/subscribe/success"
                  element={
                    <ProtectedRoute>
                      <SubscribeSuccess />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/watch/:episodeId"
                  element={
                    <ProtectedRoute>
                      <VideoPlayer />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings/sessions"
                  element={
                    <ProtectedRoute>
                      <LoginSessions />
                    </ProtectedRoute>
                  }
                />

                {/* Creator routes */}
                {/* NOTE: auth-only, NOT role-gated. An invited user is still role
                    "user" until they accept, and the invitation-accept form lives
                    inside CreatorDashboard. A role gate here bounced them to
                    /dashboard, so the ✓ on the invitation appeared to do nothing.
                    The page itself branches: profile → dashboard, pending invite →
                    accept form, neither → "only by invitation" notice. */}
                <Route
                  path="/creator"
                  element={
                    <ProtectedRoute>
                      <CreatorDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requireRole={['admin']}>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />

                {/* Playlist routes */}
                <Route
                  path="/playlists"
                  element={
                    <ProtectedRoute>
                      <Playlists />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/playlists/:playlistId"
                  element={
                    <ProtectedRoute>
                      <PlaylistDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Share routes - /v/ is canonical, /s/ kept for backward compat */}
                <Route path="/v/:shareCode" element={<SharedEpisode />} />
                <Route path="/s/:shareCode" element={<SharedEpisode />} />

                {/* Legal pages - public access */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route
                  path="/creator-agreement"
                  element={
                    <ProtectedRoute requireRole={['creator', 'admin']}>
                      <CreatorAgreement />
                    </ProtectedRoute>
                  }
                />
                <Route path="/content-guidelines" element={<ContentGuidelines />} />

                {/* Catch all - redirect to landing */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
            </main>

            {/* Upload Manager - handles background upload queue */}
            <UploadManager />

            {/* Floating Player */}
            <FloatingPlayer />

            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  background: '#1F1F22',
                  color: '#EDEDED',
                  border: '1px solid #2A2A2E',
                },
              }}
            />
          </div>
        </BrowserRouter>
      </WatchProgressProvider>
      </FreeTrialProvider>
    </AuthProvider>
  );
}

export default App;
